import React from "react";
import { RCCButton, RCCCheckBox, RCCColorPicker, RCCGrid, RCCHidden, RCCHookToast, RCCRadio, RCCSwitch, RCCTooltip } from "react-common-components";
import Select from "src/components/Select";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import LovIndicatorFields from "src/components/Lov/IndicatorFields";
import { useTranslation } from "src/context/Translations/context";
import { css } from "styled-components";
import { v4 } from "uuid";
import returnColor from "src/utils/returnColor";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { AccumulateType, AxisY, GraphicType, IIqtbc002 } from "../interface";
import { colorPickerStyle, FieldOnRight } from "../styles";

export interface ITabFieldsItem{
    index: number;
    field: IIqtbc002;
    lovFields: any;
    selectedIndicator: number;
    selectedFieldtoGoal: string;
    simpleField: boolean;
    errors:any;
    control:any;
    register:any;
    setValue: any;
    fieldsWatch: any;
    updateField: Function;
    removeField: Function;
    getSrcChartType: Function;
	refModals: any;
}

const TabFieldsItem: React.FC<ITabFieldsItem> = ({
	index,
	field,
	lovFields,
	selectedIndicator,
	selectedFieldtoGoal,
	simpleField,
	control,
	register,
	errors,
	setValue,
	fieldsWatch,
	updateField,
	removeField,
	getSrcChartType,
	refModals
}) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const returnValue = (lovValue :any) : string => lovValue?.value?.toString() || lovValue?.toString() || "";

	const onCallbackChangeFields = (item: any) => {
		const label = `${item.iqb001Descricao}(${item.iqb001Codigo})`;
		updateField(label, index);
	};

	const returnValueForLov = (iqb001Id: any, description?: string) => {
		if(iqb001Id && description)
			return { value: returnValue(iqb001Id), label: description };
		if(iqb001Id)
			return returnValue(iqb001Id);
		return undefined;
	};

	return(
		<>
			<SortableItem id={field.id}>
				<hr style={{marginTop:"10px"}}/>
				<RCCGrid container item 
					justifyContent="space-between"
				>
					<RCCGrid 
						container 
						item
						wrap="nowrap"
						alignItems="flex-end"
						xs={12}
						sm={5}
						md={6}
					>
						<LovIndicatorFields
							key={v4()}
							name={`fields.${index}.iqc002Iqb001Id`}
							label={translate("Campo")}
							control={control}
							error = {errors.fields && errors?.fields[index]?.iqc002Iqb001Id}
							defaultValue={returnValueForLov(field.iqc002Iqb001Id,field.description)}
							onChangeCapture={(newValue:any) => {
								updateField(newValue?.label, index);
							}}
							isCreatable
							required
						/>
					</RCCGrid>
					<RCCGrid container item 
						alignItems="flex-end" 
						justifyContent="center"
						xs={6}
						sm={3}
						md={2}
					>
						<RCCCheckBox
							name={`checkbox-${index}`}
							checked={selectedFieldtoGoal === field.id}
							onChange={() => {setValue("selectedGoalField",field.id);}}
							label={translate("Comparar com meta")}
						/>
						<input 
							{...register("selectedGoalField")} 
							name="selectedGoalField"
							value={selectedFieldtoGoal}
							type="hidden"
						/>
					</RCCGrid>
					<RCCGrid 
						container 
						item
						alignItems="flex-end"
						justifyContent="flex-end"
						xs={6}
						sm={4}
						md={4}
					>
						<RCCHidden xsUp={field.showConfig || field.showGraphics}>
							<SortableHandle>
								<RCCTooltip
									color="grey"
									text={translate("Ordenar")}
								>
									<RCCButton
										color="transparent"
										icon={{
											name: "arrows",
											color: "var(--grey)"
										}}
										layout="isRounded"
									/>
								</RCCTooltip>	
							</SortableHandle>
						</RCCHidden>
						{!simpleField &&
						<>
							{field.iqc002Iqb001Id &&
								<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE} path="/indicators/fields">
									<RCCTooltip
										text={translate("Alterar campo")}
									>
										<RCCButton
											icon={{name:"pencil"}}
											size="sm"
											layout="isRounded"
											onClick={
												() => {
													const idField = Number(returnValue(field.iqc002Iqb001Id));
													if(!idField || idField <= 0){
														addToast({
															color: "yellow",
															description: translate("Selecione um campo para editá-lo"),
															title: translate("Atenção")
														});
														return;
													}	
													refModals.current?.actionButtonChange({
														iqb001Id: idField,
														iqb001Codigo:"",
														iqb001Descricao:"",
														iqb001Label:""
													}, 
													onCallbackChangeFields, 
													selectedIndicator
													);
												}
											}
										/>
									</RCCTooltip>
								</CheckPermission>
							}
							<RCCTooltip
								text={translate("Configurações")}
								color={field.showConfig ? "blue":"grey"}
							>
								<RCCButton 
									onClick={() => setValue(`fields.${index}.showConfig`,!field.showConfig)} 
									size="sm" 
									color={field.showConfig ? "blue":"grey"}
									icon={{name:"settings"}} 
									layout="isRounded"
								/>
							</RCCTooltip>
							<RCCTooltip
								text={translate("Gráficos")}
								color={field.showGraphics ? "blue":"grey"}
							>
								<RCCButton 
									onClick={() => setValue(`fields.${index}.showGraphics`,!field.showGraphics)} 
									size="sm" 
									color={field.showGraphics ? "blue":"grey"}
									icon={{name:"chartBar"}} 
									layout="isRounded"
								/>
							</RCCTooltip>
						</>
						}
						<RCCTooltip
							text={translate("Remover campo")}
							color="red"
						>
							<RCCButton 
								onClick={() => removeField(field.id)} 
								size="sm" 
								color="red" 
								icon={{name:"trash"}} 
								layout="isRounded"
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
				{field.showConfig &&
			<RCCGrid container item alignItems="flex-end">
				<RCCGrid container item sm={6} md={4}>
					<Select
						control={control}
						name={`fields.${index}.iqc002Tipoacum`}
						label={translate("Tipo de acumulador")}
						defaultValue={field?.iqc002Tipoacum||AccumulateType.AVERAGE}
						error = {errors.fields && errors?.fields[index]?.iqc002Tipoacum}
						options={[
							{value:AccumulateType.AVERAGE,label:translate("Média")},
							{value:AccumulateType.LAST_VALUE,label:translate("Ultimo valor")},
							{value:AccumulateType.SUM,label:translate("Soma")},
							{value:AccumulateType.FORMULA,label:translate("Fórmula")}
						]}
						styleInline="padding-right: 8px;"
					/>
				</RCCGrid>
				<RCCGrid container item sm={6} md={4}>
					<RCCSwitch
						name={`fields.${index}.iqc002Mostrartabela`}
						register={register}
						label={translate("Mostrar em tabela")}
						defaultChecked={!!field?.iqc002Mostrartabela}
						labelPosition="end"
					/>
				</RCCGrid>
				<RCCSwitch
					name={`fields.${index}.iqc002Utilizarcamposbase`}
					register={register}
					label={translate("Utilizar Campos Base")}
					defaultChecked={!!field?.iqc002Utilizarcamposbase}
					labelPosition="end"
				/>
				<RCCHidden xsUp={!field.iqc002Utilizarcamposbase}>
					<RCCGrid container item>
						<RCCGrid container item 
							sm={6}
						> 
							<Select
								name={`fields.${index}.iqc002Dividendo`}
								label={translate("Dividendo")}
								control={control}
								defaultValue={returnValue(field.iqc002Dividendo)}
								error = {errors.fields && errors?.fields[index]?.iqc002Dividendo}
								options={lovFields}
								required
							/>
						</RCCGrid>		
						<RCCGrid container item sm={6} styleInline={FieldOnRight}> 
							<Select
								name={`fields.${index}.iqc002Divisor`}
								label={translate("Divisor")}
								control={control}
								defaultValue={returnValue(field.iqc002Divisor)}
								error = {errors.fields && errors?.fields[index]?.iqc002Divisor}
								options={lovFields}
								required
							/>
						</RCCGrid>
					</RCCGrid>
				</RCCHidden>
			</RCCGrid>
				}
				{field.showGraphics &&
			<RCCGrid container item>
				<RCCGrid container item sm={6} md={4}>
					<RCCSwitch
						name={`fields.${index}.iqc002Mostragrafico`}
						register={register}
						label={translate("Mostrar gráfico")}
						defaultChecked={field?.iqc002Mostragrafico}
						labelPosition="end"
					/>
				</RCCGrid>
				<RCCGrid container item sm={6} md={4}>
					<RCCSwitch
						name={`fields.${index}.iqc002Acumular`}
						register={register}
						label={translate("Mostrar gráfico acumulador")}
						defaultChecked={field?.iqc002Acumular}
						labelPosition="end"
					/>
				</RCCGrid>
				<RCCGrid container item sm={6} md={4} justifyContent="space-between">
					<RCCSwitch
						name={`fields.${index}.iqc002Mostrarvalor`}
						register={register}
						label={translate("Mostrar valor gráfico")}
						defaultChecked={field?.iqc002Mostrarvalor}
						labelPosition="end"
					/>
					<RCCGrid container item styleInline={css`padding-bottom:5px; padding-left:0.313rem;`} alignItems="center" justifyContent="flex-end" style={{flex: 1}}>
						<RCCColorPicker
							name={`fields.${index}.iqc002CorGrafico`}
							control={control}
							label={translate("Cor")}
							labelPosition="start"
							colorPickerProps ={{color:returnColor(field.iqc002CorGrafico||"#ffffff0")}}
							defaultValue = {returnColor(field.iqc002CorGrafico||"")}
							styleInline={colorPickerStyle}
						/>
						{field.iqc002CorGrafico && (
							<RCCTooltip
								text={translate("Limpar cor")}
								color="yellow"	
							>
								<RCCButton 
									onClick={() => setValue(`fields.${index}.iqc002CorGrafico`,"")} 
									size="xs" 
									color="transparent" 
									icon={{
										name:"broom",
										size:14,
										color: "var(--yellow)"
									}} 
									layout="isRounded"
								/>
							</RCCTooltip>
						)}
					</RCCGrid>
				</RCCGrid>			
				<RCCGrid container item sm={6} md={4}>
					{field?.iqc002Mostragrafico && (
						<>
							<RCCRadio
								register={register}
								name={`fields.${index}.iqc002Tipografico`}
								label={translate("Tipo de gráfico")}
								color="purple"
								direction="vertical" 
								defaultValue={field?.iqc002Tipografico?.toString()||GraphicType.COLUMN}
								options={[
									{value:GraphicType.AREA,label:translate("Área")},
									{value:GraphicType.COLUMN,label:translate("Coluna")},
									{value:GraphicType.LINE,label:translate("Linha")}
								]}
							/>
							<img 	
								src={getSrcChartType(fieldsWatch[index].iqc002Tipografico)} 
								alt="Chart" 
								style={{width:"4rem", marginLeft:"0.5rem"}}
							/>
						</>
					)}
				</RCCGrid>
				<RCCGrid container item sm={6} md={4}>
					{field?.iqc002Acumular && (
						<>
							<RCCRadio
								register={register}
								name={`fields.${index}.iqc002Tpgrafacum`}
								label={translate("Tipo de gráfico acumulador")}
								color="purple"
								direction="vertical" 
								defaultValue={field?.iqc002Tpgrafacum?.toString()||GraphicType.COLUMN}
								options={[
									{value:GraphicType.AREA,label:translate("Área")},
									{value:GraphicType.COLUMN,label:translate("Coluna")},
									{value:GraphicType.LINE,label:translate("Linha")}
								]}
							/>
							<img 	
								src={getSrcChartType(fieldsWatch[index].iqc002Tpgrafacum)} 
								alt="Chart" 
								style={{width:"4rem",  marginLeft:"0.5rem"}}
							/> 
						</>
					)}
				</RCCGrid>
				<RCCGrid container item sm={6} md={4}>
					<RCCRadio
						register={register}
						name={`fields.${index}.iqc002Eixoygrafico`}
						label={translate("Eixo Y")}
						color="purple"
						direction="vertical"
						defaultValue={field?.iqc002Eixoygrafico?.toString()||AxisY.LEFT}
						options={[
							{value:AxisY.LEFT,label:translate("Esquerda")},
							{value:AxisY.RIGTH,label:translate("Direita")}
						]}
					/>
				</RCCGrid>	
			</RCCGrid>
				}
			</SortableItem>
		</>
	);
};

export default TabFieldsItem;
